/**
 * The persistent audio graph.
 *
 * Built ONCE and never rebuilt. Every hardware change is a postMessage to an
 * existing node, which is what makes swapping components mid-playback free of
 * clicks, gaps and playhead resets (spec 2, 14, 15).
 *
 *   source ---------+
 *   generator ------+--> sourceGain --+--> [chain A] --> gainA --+
 *                                     |                          +--> tap
 *                                     +--> [chain B] --> gainB --+     |
 *                                                                      v
 *                                                       master --> limiter --> out
 *
 * Each chain is: dac -> amp -> output -> spatial. Both chains always exist and
 * always process, so A/B is a gain crossfade between two already-running
 * signal paths rather than a graph edit.
 */

import type { CompiledSystem } from '../types/compiled';
import type { SystemSlotKey } from '../types/system';

/**
 * DSP module load order. 00-kernel must come first: it publishes the shared
 * primitives on globalThis that every stage processor reads.
 *
 * Resolved against the document base rather than the server root, so the built
 * app works from a subdirectory (a shared folder, a project page, a path behind
 * a reverse proxy) and not only from the root of its own origin.
 */
const WORKLET_NAMES = [
  '00-kernel.js',
  '10-dac.js',
  '20-amp.js',
  '30-output.js',
  '40-spatial.js',
  '50-analyser.js',
  '60-utility.js',
];

export const WORKLET_FILES = WORKLET_NAMES.map((n) =>
  typeof document !== 'undefined' && document.baseURI
    ? new URL(`worklets/${n}`, document.baseURI).href
    : `/worklets/${n}`,
);

export interface StageStatus {
  stage: string;
  cpuMs: number | null;
  blockMs: number;
  profileId?: string;
  bypassed: boolean;
  idle: boolean;
  transitioning: boolean;
  peak: number;
  error?: string;
}

export type StageName = 'dac' | 'amp' | 'output' | 'spatial';

export class ChainNodes {
  readonly dac: AudioWorkletNode;
  readonly amp: AudioWorkletNode;
  readonly output: AudioWorkletNode;
  readonly spatial: AudioWorkletNode;
  readonly gain: GainNode;
  readonly status = new Map<string, StageStatus>();

  constructor(
    ctx: BaseAudioContext,
    readonly key: SystemSlotKey,
    onStatus: (key: SystemSlotKey, s: StageStatus) => void,
    onError: (key: SystemSlotKey, stage: string, message: string) => void,
  ) {
    const opts = (stageName: string): AudioWorkletNodeOptions => ({
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
      processorOptions: { stageName },
    });

    this.dac = new AudioWorkletNode(ctx, 'dac-stage', opts('dac'));
    this.amp = new AudioWorkletNode(ctx, 'amp-stage', opts('amp'));
    this.output = new AudioWorkletNode(ctx, 'output-stage', opts('output'));
    this.spatial = new AudioWorkletNode(ctx, 'spatial-stage', opts('spatial'));
    this.gain = ctx.createGain();
    this.gain.gain.value = key === 'A' ? 1 : 0;

    this.dac.connect(this.amp);
    this.amp.connect(this.output);
    this.output.connect(this.spatial);
    this.spatial.connect(this.gain);

    for (const [name, node] of this.entries()) {
      node.port.onmessage = (e) => {
        const m = e.data;
        if (!m) return;
        if (m.type === 'status') {
          const s: StageStatus = {
            stage: name,
            cpuMs: m.cpuMs,
            blockMs: m.blockMs,
            profileId: m.profileId,
            bypassed: !!m.bypassed,
            idle: !!m.idle,
            transitioning: !!m.transitioning,
            peak: m.peak ?? 0,
            error: m.error || undefined,
          };
          this.status.set(name, s);
          onStatus(this.key, s);
        } else if (m.type === 'error') {
          onError(this.key, name, m.message);
        }
      };
      node.onprocessorerror = () => {
        onError(this.key, name, `AudioWorklet processor "${name}" threw and has been stopped. The stage is now passing audio through unprocessed.`);
      };
    }
  }

  entries(): Array<[StageName, AudioWorkletNode]> {
    return [
      ['dac', this.dac],
      ['amp', this.amp],
      ['output', this.output],
      ['spatial', this.spatial],
    ];
  }

  get input(): AudioNode {
    return this.dac;
  }

  /**
   * Push a compiled system into the chain. Each stage crossfades internally
   * over `transitionMs`, so this never interrupts the signal.
   *
   * Payloads are structured-CLONED, not transferred. Transferring would be
   * marginally cheaper, but it detaches the sender's ArrayBuffers -- and those
   * same arrays are what Engineering Mode plots to show the filter that is
   * actually running. A detached FIR plots as nothing, which would quietly turn
   * the most important honesty feature in the application into an empty graph.
   * A compiled chain is on the order of 30 kB; cloning it on a hardware change
   * costs microseconds and is not worth trading correctness for.
   */
  applySystem(sys: CompiledSystem, transitionMs: number, only?: StageName[]): void {
    const send = (name: StageName, node: AudioWorkletNode, payload: unknown) => {
      if (only && !only.includes(name)) return;
      try {
        node.port.postMessage({ type: 'profile', payload, transitionMs });
      } catch (err) {
        throw new Error(`Could not send the ${name} profile to its worklet: ${(err as Error).message}`);
      }
    };
    send('dac', this.dac, sys.dac);
    send('amp', this.amp, sys.amp);
    send('output', this.output, sys.output);
    send('spatial', this.spatial, sys.spatial);
  }

  /** Skip all DSP in this chain while it is silent. */
  setIdle(idle: boolean): void {
    for (const [, node] of this.entries()) node.port.postMessage({ type: 'idle', value: idle });
  }

  setBypass(stage: StageName, bypassed: boolean): void {
    const node = this.entries().find(([n]) => n === stage)?.[1];
    node?.port.postMessage({ type: 'bypass', value: bypassed });
  }

  setParam(stage: StageName, name: string, value: number): void {
    const node = this.entries().find(([n]) => n === stage)?.[1];
    node?.port.postMessage({ type: 'param', name, value });
  }

  disconnect(): void {
    for (const [, node] of this.entries()) {
      node.port.onmessage = null;
      node.disconnect();
    }
    this.gain.disconnect();
  }
}
